import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { authorizedFetch } from "../auth";

interface ReportDetail {
  listing: {
    contactId: string;
    callerDisplayName: string;
    dealer: string;
    headline: string;
    startedAt: string;
    durationSeconds: number;
    actionCount: number;
    status: string;
  };
  report: ReportBody | null;
  actions: { actions: ActionItem[] } | null;
  transcript: TranscriptBody | null;
  artefacts: Record<string, string>;
}

interface ReportBody {
  headline: string;
  executiveSummary: string;
  dealer: { name: string; location: string | null };
  attendees: Array<{ name: string; role: string | null }>;
  discussionTopics: Array<{
    title: string;
    summary: string;
    keyPoints: string[];
    decisions: string[];
  }>;
  commercialContext: {
    opportunities: Array<{ description: string; estimatedValueGbp: number | null; confidence: string }>;
    risks: Array<{ description: string; severity: string }>;
    competitorMentions: Array<{ competitor: string; context: string }>;
  };
  customerSentiment: string;
  featureRequests: string[];
  followUpRequired: boolean;
  intelligenceNotes: string;
  metadata: {
    callerPhone: string;
    callStartedAt: string;
    callEndedAt: string;
    callDurationSeconds: number;
  };
}

interface ActionItem {
  actionId: string;
  owner: string;
  ownerRole: string;
  action: string;
  context: string | null;
  dueDate: string | null;
  priority: "urgent" | "high" | "medium" | "low";
}

interface TranscriptBody {
  turns: Array<{
    turnIndex: number;
    participant: "Caller" | "Otto" | "Unknown";
    startOffsetMs: number;
    text: string;
    sentiment: string | null;
  }>;
}

function formatDate(iso: string): string {
  if (!iso) return "\u2014";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", timeZone: "Europe/London",
  });
}

function formatOffset(ms: number): string {
  const s = ms / 1000;
  const m = Math.floor(s / 60);
  return `${m}:${(s - m * 60).toFixed(1).padStart(4, "0")}`;
}

export function ReportDetailPage(): React.JSX.Element {
  const { contactId } = useParams<{ contactId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ["report", contactId],
    queryFn: async (): Promise<ReportDetail> => {
      const resp = await authorizedFetch(`/api/reports/${contactId}`);
      if (!resp.ok) throw new Error(`API returned ${resp.status}`);
      return resp.json() as Promise<ReportDetail>;
    },
    enabled: !!contactId,
  });

  if (isLoading) return <div className="loading">Loading meeting&hellip;</div>;
  if (error || !data) return <div className="error">Failed to load: {String(error)}</div>;

  const { listing, report, actions, transcript, artefacts } = data;

  return (
    <>
      <Link to="/" className="back-link">&larr; Back to all meetings</Link>

      <div className="card">
        <h2>{listing.headline}</h2>
        <div className="meta-grid">
          <div className="cell"><span className="label">Caller</span><span className="value">{listing.callerDisplayName}</span></div>
          <div className="cell"><span className="label">Dealer</span><span className="value">{listing.dealer}</span></div>
          <div className="cell"><span className="label">Meeting</span><span className="value">{formatDate(listing.startedAt)}</span></div>
          <div className="cell"><span className="label">Duration</span><span className="value">{Math.round(listing.durationSeconds / 60)} min {Math.round(listing.durationSeconds % 60)} sec</span></div>
        </div>
        <div className="artefact-buttons">
          <a className="btn primary" href={artefacts["report.pdf"]} target="_blank" rel="noreferrer">Meeting report (PDF)</a>
          <a className="btn" href={artefacts["transcript.pdf"]} target="_blank" rel="noreferrer">Full transcript (PDF)</a>
          <a className="btn" href={artefacts["report.json"]} target="_blank" rel="noreferrer">Structured JSON</a>
        </div>
      </div>

      {report && (
        <div className="card">
          <h3>Executive summary</h3>
          <p>{report.executiveSummary}</p>

          {report.attendees.length > 0 && (
            <>
              <h3>Attendees</h3>
              <ul>
                {report.attendees.map((a, i) => (
                  <li key={i}><strong>{a.name}</strong>{a.role ? ` \u2014 ${a.role}` : ""}</li>
                ))}
              </ul>
            </>
          )}

          {report.discussionTopics.length > 0 && (
            <>
              <h3>Discussion</h3>
              {report.discussionTopics.map((topic, i) => (
                <div key={i} className="topic">
                  <h4>{topic.title}</h4>
                  {topic.summary && <p>{topic.summary}</p>}
                  {topic.keyPoints.length > 0 && (
                    <ul>{topic.keyPoints.map((p, j) => (<li key={j}>{p}</li>))}</ul>
                  )}
                  {topic.decisions.length > 0 && (
                    <ul className="decisions">{topic.decisions.map((d, j) => (<li key={j}>{d}</li>))}</ul>
                  )}
                </div>
              ))}
            </>
          )}

          {report.commercialContext.opportunities.length > 0 && (
            <>
              <h3>Opportunities</h3>
              <ul>
                {report.commercialContext.opportunities.map((o, i) => (
                  <li key={i}>{o.description}{o.estimatedValueGbp ? ` \u2014 est. \u00a3${o.estimatedValueGbp.toLocaleString()}` : ""} <span className={`badge badge-priority-${o.confidence}`}>{o.confidence}</span></li>
                ))}
              </ul>
            </>
          )}

          {report.commercialContext.risks.length > 0 && (
            <>
              <h3>Risks</h3>
              <ul>
                {report.commercialContext.risks.map((r, i) => (
                  <li key={i}>{r.description} <span className={`badge badge-priority-${r.severity}`}>{r.severity}</span></li>
                ))}
              </ul>
            </>
          )}

          {report.intelligenceNotes && (
            <>
              <h3>Intelligence notes</h3>
              <p>{report.intelligenceNotes}</p>
            </>
          )}

          <h3>Customer sentiment</h3>
          <p><span className={`badge badge-sentiment-${report.customerSentiment}`}>{report.customerSentiment}</span></p>
        </div>
      )}

      {actions && actions.actions.length > 0 && (
        <div className="card">
          <h2>Actions ({actions.actions.length})</h2>
          <ul className="actions-list">
            {actions.actions.map((a) => (
              <li key={a.actionId} className={`action-card priority-${a.priority}`}>
                <div className="title">{a.action}</div>
                <div className="owner-row">
                  <strong>{a.owner}</strong> ({a.ownerRole})
                  {a.dueDate ? ` \u00b7 due ${a.dueDate}` : ""}
                  {" \u00b7 "}
                  <span className={`badge badge-priority-${a.priority}`}>{a.priority}</span>
                </div>
                {a.context && <div className="context">{a.context}</div>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {transcript && transcript.turns.length > 0 && (
        <div className="card">
          <h2>Transcript</h2>
          {transcript.turns.map((t) => (
            <div key={t.turnIndex} className="transcript-turn">
              <div className="stamp">{formatOffset(t.startOffsetMs)}</div>
              <div>
                <div className={`speaker speaker-${t.participant.toLowerCase()}`}>{t.participant === "Caller" ? listing.callerDisplayName : "Otto"}</div>
                <div className="text">{t.text || <em>(silence)</em>}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
